import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { Play, ExternalLink } from "lucide-react";
import { CATEGORY_GROUPS } from "../lib/assets";
import SectionLabel from "./SectionLabel";

const ImageTile = ({ item, idx, ratio = "aspect-[4/3]" }) => {
  const isLinked = Boolean(item.link);
  const Wrapper = isLinked ? "a" : "div";
  const wrapperProps = isLinked
    ? {
        href: item.link,
        target: "_blank",
        rel: "noopener noreferrer",
        "aria-label": `Open ${item.title} in a new tab`,
      }
    : {};

  const tileRatio = item.ratio || ratio;
  const fitClass = item.fit === "contain" ? "object-contain p-6" : "object-cover";

  return (
    <motion.figure
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: (idx % 3) * 0.07 }}
      viewport={{ once: true, margin: "-80px" }}
      className="group relative w-full overflow-hidden border border-black/30 bg-[#CCD6DC]"
      data-testid={`work-tile-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Wrapper {...wrapperProps} className="block">
        <div className={`relative w-full ${tileRatio} overflow-hidden`}>
          <img
            src={item.src}
            alt={item.title}
            loading="lazy"
            className={`proj-img absolute inset-0 w-full h-full ${fitClass}`}
          />
          <div className="pointer-events-none absolute inset-0 opacity-30 group-hover:opacity-0 transition-opacity duration-500">
            <div className="absolute top-3 left-3 w-3 h-px bg-black" />
            <div className="absolute top-3 left-3 w-px h-3 bg-black" />
            <div className="absolute top-3 right-3 w-3 h-px bg-black" />
            <div className="absolute top-3 right-3 w-px h-3 bg-black" />
            <div className="absolute bottom-3 left-3 w-3 h-px bg-black" />
            <div className="absolute bottom-3 left-3 w-px h-3 bg-black" />
            <div className="absolute bottom-3 right-3 w-3 h-px bg-black" />
            <div className="absolute bottom-3 right-3 w-px h-3 bg-black" />
          </div>

          {/* External link badge - shows on linked tiles */}
          {isLinked && (
            <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 border border-black/70 bg-white/80 backdrop-blur-sm opacity-90">
              <ExternalLink size={12} strokeWidth={1.5} className="text-[#AE519F]" />
              <span
                className="mono-label"
                style={{ color: "#AE519F", fontSize: "10px" }}
              >
                LIVE
              </span>
            </div>
          )}
        </div>
        <figcaption className="flex items-center justify-between px-4 py-3 border-t border-black/30">
          <span
            className="text-black text-sm md:text-[15px]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {item.title}
          </span>
          <span className="mono-label">{isLinked ? "VISIT SITE" : "VIEW"}</span>
        </figcaption>
      </Wrapper>
    </motion.figure>
  );
};

const VideoTile = ({ item, idx }) => {
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  const isPortrait = item.orientation === "portrait";
  const ratioClass = isPortrait ? "aspect-[9/16]" : "aspect-video";

  const onPlayClick = () => {
    const v = videoRef.current;
    if (!v) return;
    setStarted(true);
    v.controls = true;
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  };

  return (
    <motion.figure
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: (idx % 2) * 0.08 }}
      viewport={{ once: true, margin: "-80px" }}
      className="group relative overflow-hidden border border-black/30 bg-[#CCD6DC] mx-auto w-full"
      style={{ maxWidth: isPortrait ? "300px" : "560px" }}
      data-testid={`work-tile-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className={`relative w-full ${ratioClass} overflow-hidden bg-[#1a1a1a]`}>
        <video
          ref={videoRef}
          src={item.src}
          loop
          playsInline
          preload="metadata"
          controls={started}
          className={`absolute inset-0 w-full h-full object-contain ${
            started ? "" : "proj-img"
          }`}
        />

        {/* Click-to-play overlay - disappears once started */}
        {!started && (
          <button
            type="button"
            onClick={onPlayClick}
            data-testid={`play-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            aria-label={`Play ${item.title}`}
            className="absolute inset-0 flex flex-col items-center justify-center gap-4 group/btn focus:outline-none"
          >
            <span className="flex items-center justify-center w-16 h-16 border border-white/70 bg-black/40 backdrop-blur-sm group-hover/btn:bg-[#AE519F] group-hover/btn:border-[#AE519F] transition-colors">
              <Play
                className="text-white ml-0.5 group-hover/btn:text-white"
                size={22}
                strokeWidth={1.4}
                fill="currentColor"
              />
            </span>
            <span className="mono-label text-white">CLICK · PLAY WITH SOUND</span>

            {/* Wireframe corner ticks */}
            <span className="pointer-events-none absolute inset-0 opacity-60">
              <span className="absolute top-3 left-3 w-3 h-px bg-white" />
              <span className="absolute top-3 left-3 w-px h-3 bg-white" />
              <span className="absolute top-3 right-3 w-3 h-px bg-white" />
              <span className="absolute top-3 right-3 w-px h-3 bg-white" />
              <span className="absolute bottom-3 left-3 w-3 h-px bg-white" />
              <span className="absolute bottom-3 left-3 w-px h-3 bg-white" />
              <span className="absolute bottom-3 right-3 w-3 h-px bg-white" />
              <span className="absolute bottom-3 right-3 w-px h-3 bg-white" />
            </span>
          </button>
        )}
      </div>
      <figcaption className="flex items-center justify-between px-4 py-3 border-t border-black/30">
        <span
          className="text-black text-sm md:text-[15px]"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {item.title}
        </span>
        <span className="mono-label">FILM</span>
      </figcaption>
    </motion.figure>
  );
};

const Tile = ({ item, idx, ratio }) =>
  item.type === "video" ? (
    <VideoTile item={item} idx={idx} />
  ) : (
    <ImageTile item={item} idx={idx} ratio={ratio} />
  );

const CategorySection = ({ group }) => {
  const isLogos = group.id === "logos";
  const isContent = group.id === "content";

  const landscapeItems = isContent
    ? group.items.filter((i) => i.orientation !== "portrait")
    : [];
  const portraitItems = isContent
    ? group.items.filter((i) => i.orientation === "portrait")
    : [];

  const gridClass = isLogos
    ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-px bg-black/30"
    : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/30 items-start";
  const itemRatio = isLogos ? "aspect-square" : "aspect-[4/3]";

  return (
    <div
      data-testid={`work-category-${group.id}`}
      className="border-t border-black/40 py-16 md:py-20"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10 items-end">
        <div className="lg:col-span-2">
          <div
            className="text-[#AE519F] font-archivo font-black text-6xl md:text-7xl"
            style={{ fontFamily: "Archivo, sans-serif", letterSpacing: "-0.04em" }}
          >
            {group.number}
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="mono-label mb-3">{group.label}</div>
          <h3
            className="text-black text-2xl md:text-3xl lg:text-4xl"
            style={{
              fontFamily: "Archivo, sans-serif",
              fontWeight: 800,
              letterSpacing: "-0.02em",
              lineHeight: 1.05,
            }}
          >
            {group.title}
          </h3>
        </div>
        <div className="lg:col-span-3 flex lg:justify-end">
          <div className="mono-label">
            {`${String(group.items.length).padStart(2, "0")} / ${
              isContent ? "FILMS" : "PROJECTS"
            }`}
          </div>
        </div>
      </div>

      {isContent ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start">
          <div className="flex flex-col gap-8 md:gap-12 items-center">
            {landscapeItems.map((it, i) => (
              <Tile key={it.src} item={it} idx={i} />
            ))}
          </div>
          <div className="flex flex-col gap-8 md:gap-12 items-center">
            {portraitItems.map((it, i) => (
              <Tile key={it.src} item={it} idx={i} />
            ))}
          </div>
        </div>
      ) : (
        <div className={gridClass}>
          {group.items.map((it, i) => (
            <Tile key={it.src} item={it} idx={i} ratio={itemRatio} />
          ))}
        </div>
      )}
    </div>
  );
};

const WorkShowcase = () => {
  return (
    <section
      id="work"
      data-testid="work-showcase"
      className="relative py-24 md:py-28 border-t-2 border-black"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-7">
            <SectionLabel className="mb-6" testid="label-work">
              [ 04 ] - VISUAL WORK
            </SectionLabel>
            <h2
              className="font-archivo font-black h-tighter text-4xl md:text-5xl lg:text-6xl text-black"
              style={{ fontFamily: "Archivo, sans-serif" }}
            >
              Static idea →
              <br />
              <span className="text-[#AE519F]">Real-world execution.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p
              className="text-black/70 text-base md:text-lg max-w-md"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Every project below moves from concept to delivered reality. The
              proof layer is intentionally physical - exhibitions, retail, packs,
              films - because that's where most agencies stop.
            </p>
          </div>
        </div>

        {CATEGORY_GROUPS.map((g) => (
          <CategorySection key={g.id} group={g} />
        ))}
      </div>
    </section>
  );
};

export default WorkShowcase;
